import onDocumentLoaded from "../../lib/onDocumentLoaded";
import AutocompleteHelper, {AutocompleteItem, AutocompleteSettings} from "./AutocompleterHelper";
import * as $ from "jquery";
import {Analytics} from '@plaudit/analytics';

import * as lunr from 'lunr';
import replaceStemerWithOneThatKeepsOriginalAndStems from '../../lib/lunr/replaceStemerWithOneThatKeepsOriginalAndStems';

interface PlauditAutocompleteOption extends AutocompleteItem {
	href?: string;
	// fields?: object;
}

export default class Autocompleter {
	private documents: any = null;
	private lunrIndex: lunr.Index|null = null;

	public static setup() {
		onDocumentLoaded(document, () => {
			// Grab all autocomplete input fields on a page
			const suggestionBoxes = document.body.querySelectorAll("input:not(.autocompleter--isSetup).autocomplete-input");

			for (let i = 0; i < suggestionBoxes.length; i++) {
				const input = suggestionBoxes.item(i) as HTMLInputElement;

				const autocompleter = new Autocompleter();
				autocompleter.onReady(input);
			}
		});
	}

	// This is called when everything is ready.
	private onReady = (input: HTMLInputElement) => {
		let baseURI = window.location.origin;
		const jsonURI = baseURI + '/wp-json/adams-site/v1/service-areas';
		const emptyMsg = 'No location found. Please try a 5-digit zip code.';

		$.ajax({
			dataType: 'json',
			url: jsonURI,
			success: response => {
				this.processResponse(response);
			},
			async: false
		});
		const fetch = (text: string, update: (items: PlauditAutocompleteOption[]) => void) => {
			const originalText = text.trim();

			// Add wildcard to end and between each word. e.g. 'st paul' becomes 'st* paul*'
			// Note that by default in lunr this does cause an issue with 'Rocheseter*' not matching 'Rochester'
			// because when a wildcard is used lunr doesn't do stemming. To resolve above I created replaceStemerWithOneThatKeepsOriginalAndStems
			text = originalText.replace(/\s+/g, '* ') + '*';

			// console.log('Searching for', text);

			// Conduct the search
			const matches: Array<lunr.Index.Result> = this.lunrIndex!.search(text);

			// Create results given to autocomplete / suggester
			const flattenedResults: PlauditAutocompleteOption[] = [];
			const alreadyAdded: {[key: string]: boolean} = {}; // Used to filter out non-unique results
			matches.forEach((match) => {
				const doc = this.documents[match.ref];
				let label;
				if (doc.city) {
					label = doc.city;
					// if (doc.state) {
					// 	label += ", " + doc.state;
					// }
					// if (doc.zipCodes) {
					// 	label += " (" + doc.zipCodes.join(', ') + ")";
					// }
				} else {
					label = doc.zipCodes.join(', ') ?? doc.href ?? 'NA';
				}

				if (!alreadyAdded.hasOwnProperty(label)) {
					// console.log('  	Result:', doc);
					flattenedResults.push({
						label: label,
						href: doc.href,
					});
					alreadyAdded[label] = true;
				}
			});

			if (text === '*') {
				// Instead of using scoring let's sort alphabetically
				flattenedResults.sort((a, b) => {
					if(a.label < b.label) { return -1; }
					if(a.label > b.label) { return 1; }
					return 0;
				});
			}

			update(flattenedResults);

			return;
		};

		// const validateValue = (value: string): boolean => {
		// 	// If only numbers (zip code) then require 5 digts, otherwise we require a minimum of 4 digits
		// 	if (/^\d+$/.test(value)) {
		// 		return value.length >= 5;
		// 	}
		// 	return value.length >= 4;
		// };

		const escapeHTMLChars: {[key:string]: string} = {'&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'};
		const escapeHTML = (str: string): string => str.replace(/[&<>'"]/g, tag => escapeHTMLChars[tag] || tag);

		const settings: AutocompleteSettings<PlauditAutocompleteOption> = {
			input: input,
			className: 'autocomplete',
			showOnFocus: true,
			hideOnBlur: true,
			onFetchStart: () => input.form?.querySelectorAll("input[type='submit']").forEach((submit) => submit.classList.add("btn--isWaiting")),
			onFetchEnd: () => input.form?.querySelectorAll("input[type='submit']").forEach((submit) => submit.classList.remove("btn--isWaiting")),

			// validateValue,
			fetch,
			onSelect: item => {
				if (item.href === undefined)
					return;

				// Track use of auto-complete
				let label = item.label.replace(/<\/?span( [^>]*)?>/g, '');
				Analytics.getInstance().trackEvent("Search:service-area", "Autocomplete Option Selected", [input.value, label, item.href]);

				// Change page to what was selected
				let href = item.href;
				if (!/^(?:[a-z]+:)?/i.test(href)) {
					const base = document.querySelector('base');
					if (base) {
						href = base.href;
						if (href.charAt(href.length - 1) !== '/') {
							href += '/';
						}
						href += item.href;
					}
				}
				location.href = href;
			},
			render: (item, currentValue) => {
				// Add data to the display
				const div = document.createElement("div");
				div.className = 'autocomplete__option suggestion';

				const labelWrapper = document.createElement("div");
				div.appendChild(labelWrapper);
				labelWrapper.innerHTML +=
					'<span class="autocomplete__optionLabel">' + escapeHTML(item.label) + '</span>'
					+ '<a href="' + item.href + '" class="autocomplete__detailsLink"><span class="autocomplete__detailsLinkLabel">Details</span</a>';

				return div;
			},
			debounceWait: 250,
			emptyMsg,
		};

		const autocompleteHelper = new AutocompleteHelper<PlauditAutocompleteOption>(settings);

		// Make form send to first option
		input.form?.addEventListener('submit', (event) => {
			autocompleteHelper.gotoSelectedItem();
			event.preventDefault();
		});

		// Mark that the setup is completed
		input.classList.add("autocompleter--isSetup");
	}

	private processResponse = (documents: any): void => {
		const that = this;

		// let id = -1;
		this.documents = [];
		this.lunrIndex = lunr(function () {
			this.use(replaceStemerWithOneThatKeepsOriginalAndStems);

			const refName = 'id';

			this.ref(refName);	// 'id' is default
			this.field('city');
			this.field('zipCodes');


			// Prepare that.documents
			documents.forEach((doc: any, index: number) => {
				let href = doc.slug;

				const city = doc;

				const addDocument = (zipCode?: string) => {
					// id++;
					const myHref = href ?? zipCode;
					//const id = city.city + "-" + city.state;
					const id = city.city;

					if (that.documents[id] !== undefined) {
						// Append
						if (zipCode !== undefined) {
							that.documents[id].zipCodes.push(zipCode);
						}
					} else {
						// New
						const newDoc = {
							id,
							city: city.city,
							// state: city.state,
							zipCodes: zipCode === undefined ? [] : [zipCode],
							href: myHref,
						};

						that.documents[id] = newDoc;
					}
				};

				if (city.zipCodes) {
					const zipCodes = city.zipCodes.split(',');
					zipCodes.forEach(addDocument);
				} else {
					// No zipcodes
					addDocument(undefined);
				}
			} );

			// console.log('All documents', that.documents);

			// Index that.documents
			for (const key in that.documents) {
				const doc = that.documents[key];
				console.log('Indexing', key, doc.zipCodes, doc);
				this.add(doc);
			}
		});
	}

}



